import type { Notification } from "../../types";
import type { IOperationResult } from "../types";
import { createBaseService, fromDb } from "./baseService";
import { getSupabase as getSupabaseNullable } from "../../lib/supabase";

type NotificationBase = Omit<Notification, "id">;

function requireSupabase() {
  const client = getSupabaseNullable();
  if (!client) throw new Error("Supabase not initialized. Call initSupabase() first.");
  return client;
}

const base = createBaseService<Notification, NotificationBase>({
  tableName: "notifications",
  searchColumns: ["title"],
  skipAuditFields: true,
});

export class NotificationService {
  static getAll = base.getAll;
  static get = base.get;
  static create = base.create;
  static update = base.update;
  static delete = base.delete;

  static async markRead(id: string): Promise<IOperationResult<Notification>> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("notifications")
        .update({ read: true, read_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) return { success: false, error };
      return {
        success: true,
        data: fromDb<Notification>(data as Record<string, unknown>),
      };
    } catch (error) {
      return { success: false, error };
    }
  }

  static async markAllRead(): Promise<IOperationResult<void>> {
    try {
      const supabase = requireSupabase();
      const { error } = await supabase
        .from("notifications")
        .update({ read: true, read_at: new Date().toISOString() })
        .eq("read", false);

      if (error) return { success: false, error };
      return { success: true, data: undefined };
    } catch (error) {
      return { success: false, error };
    }
  }

  static async markUnread(id: string): Promise<IOperationResult<Notification>> {
    try {
      const supabase = requireSupabase();
      const { data, error } = await supabase
        .from("notifications")
        .update({ read: false, read_at: null })
        .eq("id", id)
        .select()
        .single();

      if (error) return { success: false, error };
      return {
        success: true,
        data: fromDb<Notification>(data as Record<string, unknown>),
      };
    } catch (error) {
      return { success: false, error };
    }
  }

  static async deleteRead(): Promise<IOperationResult<void>> {
    try {
      const supabase = requireSupabase();
      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("read", true);

      if (error) return { success: false, error };
      return { success: true, data: undefined };
    } catch (error) {
      return { success: false, error };
    }
  }

  static async getUnreadCount(): Promise<IOperationResult<number>> {
    try {
      const supabase = requireSupabase();
      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("read", false);

      if (error) return { success: false, error };
      return { success: true, data: count ?? 0 };
    } catch (error) {
      return { success: false, error };
    }
  }
}
